import { useCallback } from 'react';
import { Card, CardContent, Box, AppBar, Toolbar, Typography, Button } from '@mui/material';
import { CONTENT_EDITOR } from './MassActionMessage';
import MassActionMsgImportTplItem from './MassActionMsgImportTplItem';
import useFetch from 'src/hooks/useFetch';
import { useTranslation } from 'react-i18next';
import { useCheckContentFieldsAndSelectedColumns } from 'src/components/massAction/state/massAction';
import { useMassActionState } from 'src/components/massAction/MassActionStateContext';

interface StyledAppBarProps {
  currentName: string;
  title: string;
  campaignId: string;
}

const StyledAppBar = ({ currentName, campaignId, title }: StyledAppBarProps) => (
  <AppBar
    sx={{
      position: 'relative',
      textAlign: 'left',
    }}
    color="primary"
  >
    <Toolbar style={{ paddingLeft: '8px' }}>
      <Box
        sx={{
          flex: '1',
        }}
      >
        <Typography variant="h5" color="inherit">
          {title}
        </Typography>
        <Typography variant="body2" color="inherit">
          {currentName}
          {!!campaignId && ` (${campaignId})`}
        </Typography>
      </Box>
    </Toolbar>
  </AppBar>
);

interface InitialContentSelectorProps {
  actionType: number;
  setStep: (stepId: number, newExtraProps?: { [key: string]: unknown }) => void;
  onCancel?: () => void;
}

function XplorActiveNewsInitialContentSelector({
  actionType,
  setStep,
  onCancel,
}: InitialContentSelectorProps) {
  const [{ currentMassActionId, currentName }] = useMassActionState();
  const campaignId = currentMassActionId;
  const currentCampaignName = currentName;

  const { data: forbiddenFields = [], status } = useFetch<string[]>({
    url: '/smartmessaging/listXplorNewsActionCoForbiddenFields',
  });

  const checkContentFieldsAndSelectedColumns = useCheckContentFieldsAndSelectedColumns();

  const initializeXplorActiveNewsContent = useCallback(
    (initialContentValue: string) => {
      checkContentFieldsAndSelectedColumns(initialContentValue, actionType, evaluatedFields => {
        setStep(CONTENT_EDITOR, {
          content: initialContentValue || '',
          fields: {
            ...evaluatedFields,
            fieldsValues: {
              ...evaluatedFields.fieldsValues,
              contentFields: evaluatedFields.fieldsValues.contentFields.filter(
                field => !forbiddenFields.includes(field.id)
              ),
            },
          },
        });
      });
    },
    [checkContentFieldsAndSelectedColumns, actionType, setStep, forbiddenFields]
  );

  const initEmpty = () => {
    initializeXplorActiveNewsContent('');
  };

  const { t } = useTranslation();

  if (status !== 'success') return null;
  return (
    <>
      <StyledAppBar
        currentName={currentCampaignName || ''}
        campaignId={campaignId}
        title={t(`smartmessaging.contentEditor.title.${actionType}`)}
      />
      <Box sx={{ margin: '24px 15% 0px' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: 2,
          }}
        >
          <Card
            sx={{
              width: '200px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              m: 1,
              cursor: 'pointer',
            }}
            onClick={initEmpty}
          >
            <CardContent
              sx={{
                height: '3em',
                wordBreak: 'break-word',
                wordWrap: 'normal',
                color: 'primary.main',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {t('empty_message')}
            </CardContent>
          </Card>
          <MassActionMsgImportTplItem onFileLoaded={initializeXplorActiveNewsContent} />
        </Box>
        <Box
          sx={{
            textAlign: 'right',
          }}
        >
          <Button variant="text" color="primary" style={{ margin: '2px' }} onClick={onCancel}>
            {t('smartmessaging.buttonLabel.previous')}
          </Button>
        </Box>
      </Box>
    </>
  );
}

export default XplorActiveNewsInitialContentSelector;
